import React from "react";
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalFooter,
  ModalBody,
  ModalCloseButton,
  Button,
  Text,
  VStack,
  HStack,
  Box,
  Badge,
  Divider,
  Icon,
  useColorModeValue,
  Grid,
  GridItem,
} from "@chakra-ui/react";
import { motion } from "framer-motion";
import {
  FaBuilding,
  FaUser,
  FaEnvelope,
  FaPhone,
  FaMapMarkerAlt,
  FaIdCard,
  FaCalendarAlt,
  FaCheckCircle,
  FaInfoCircle,
  FaTags,
} from "react-icons/fa";

const MotionBox = motion(Box);

const InfoCard = ({ icon, label, value, color = "gray.600" }) => {
  const cardBg = useColorModeValue("gray.50", "gray.700");
  const borderColor = useColorModeValue("gray.200", "gray.600");
  
  return (
    <MotionBox
      p={4}
      bg={cardBg}
      borderRadius="xl"
      border="1px solid"
      borderColor={borderColor}
      whileHover={{ y: -2, shadow: "lg" }}
      transition={{ duration: 0.2 }}
    >
      <HStack spacing={3} align="start">
        <Icon as={icon} color="#f57d36" boxSize={5} mt={1} />
        <VStack spacing={1} align="start" flex={1}>
          <Text fontSize="xs" fontWeight="medium" color="gray.500" textTransform="uppercase">
            {label}
          </Text>
          <Text fontSize="sm" fontWeight="600" color={color} wordBreak="break-word">
            {value || "N/A"}
          </Text>
        </VStack>
      </HStack>
    </MotionBox>
  );
};

const StatusBadge = ({ status }) => {
  let colorScheme, label;
  
  switch (status?.toLowerCase()) {
    case "created":
      colorScheme = "orange";
      label = "Created";
      break;
    case "pending":
      colorScheme = "orange";
      label = "Pending";
      break;
    case "active":
      colorScheme = "green";
      label = "Active";
      break;
    default:
      colorScheme = "gray";
      label = status || "Unknown";
  }

  return (
    <Badge
      colorScheme={colorScheme}
      px={3}
      py={1}
      borderRadius="md"
      fontSize="xs"
      fontWeight="bold"
      textTransform="uppercase"
    >
      <Icon as={FaCheckCircle} boxSize={3} mr={2} />
      {label}
    </Badge>
  );
};

const BusinessTypeBadge = ({ businessType }) => {
  let colorScheme, label;
  
  switch (businessType?.toLowerCase()) {
    case "not_yet_registered":
      colorScheme = "orange";
      label = "Not Yet Registered";
      break;
    case "registered":
      colorScheme = "green";
      label = "Registered";
      break;
    case "partnership":
      colorScheme = "blue";
      label = "Partnership";
      break;
    default:
      colorScheme = "gray";
      label = businessType || "Unknown";
  }

  return (
    <Badge
      colorScheme={colorScheme}
      px={3}
      py={1}
      borderRadius="full"
      fontSize="xs"
      fontWeight="semibold"
      variant="subtle"
    >
      {label}
    </Badge>
  );
};

const LinkedAccountDetailsModal = ({ isOpen, onClose, accountData }) => {
  const modalBg = useColorModeValue("white", "gray.800");
  const borderColor = useColorModeValue("gray.200", "gray.600");
  
  if (!accountData) return null;

  const formatDate = (timestamp) => {
    if (!timestamp) return "N/A";
    return new Date(timestamp * 1000).toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const address = accountData.profile?.addresses?.registered;
  const fullAddress = address 
    ? `${address.street1}${address.street2 && address.street2 !== "NA" ? ", " + address.street2 : ""}, ${address.city}, ${address.state}, ${address.country} - ${address.postal_code}`
    : "N/A";

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="2xl" isCentered>
      <ModalOverlay bg="blackAlpha.300" backdropFilter="blur(10px)" />
      <ModalContent
        bg={modalBg}
        borderRadius="2xl"
        boxShadow="2xl"
        border="1px solid"
        borderColor={borderColor}
        maxH="90vh"
        overflow="hidden"
      >
        <ModalHeader pb={2}>
          <VStack spacing={3} align="start">
            <HStack spacing={3} w="full" justify="start" alignItems="start">
              <HStack spacing={3}>
                <Icon as={FaBuilding} color="#f57d36" boxSize={6} />
                <VStack spacing={0} align="start">
                  <Text fontSize="xl" fontWeight="bold">
                    Linked Account Details
                  </Text>
                  <Text fontSize="sm" color="gray.500" fontWeight="normal">
                    Account ID: {accountData.id}
                  </Text>
                </VStack>
              </HStack>
              <VStack spacing={2} align="end">
                <StatusBadge status={accountData.status} />
              </VStack>
            </HStack>
          </VStack>
        </ModalHeader>
        
        <ModalCloseButton />
        
        <ModalBody py={4} overflow="auto">
          <VStack spacing={6} align="stretch">
            {/* Business Information */}
            <Box>
              <HStack spacing={2} mb={4}>
                <Icon as={FaBuilding} color="#f57d36" boxSize={4} />
                <Text fontSize="md" fontWeight="semibold">Business Information</Text>
              </HStack>
              <Grid templateColumns={{ base: "1fr", md: "repeat(2, 1fr)" }} gap={4}>
                <InfoCard
                  icon={FaBuilding}
                  label="Legal Business Name"
                  value={accountData.legal_business_name}
                />
                <InfoCard
                  icon={FaTags}
                  label="Customer Facing Name"
                  value={accountData.customer_facing_business_name}
                />
                <InfoCard
                  icon={FaInfoCircle}
                  label="Business Type"
                  value={accountData.business_type?.replace(/_/g, " ").toUpperCase()}
                />
                <InfoCard
                  icon={FaTags}
                  label="Account Type"
                  value={accountData.type?.toUpperCase()}
                />
              </Grid>
            </Box>

            <Divider />

            {/* Contact Information */}
            <Box>
              <HStack spacing={2} mb={4}>
                <Icon as={FaUser} color="#f57d36" boxSize={4} />
                <Text fontSize="md" fontWeight="semibold">Contact Information</Text>
              </HStack>
              <Grid templateColumns={{ base: "1fr", md: "repeat(2, 1fr)" }} gap={4}>
                <InfoCard
                  icon={FaUser}
                  label="Contact Name"
                  value={accountData.contact_name}
                />
                <InfoCard
                  icon={FaEnvelope}
                  label="Email Address"
                  value={accountData.email}
                />
                <GridItem colSpan={{ base: 1, md: 2 }}>
                  <InfoCard
                    icon={FaPhone}
                    label="Phone Number"
                    value={accountData.phone}
                  />
                </GridItem>
              </Grid>
            </Box>

            <Divider />

            {/* Business Profile */}
            {accountData.profile && (
              <Box>
                <HStack spacing={2} mb={4}>
                  <Icon as={FaTags} color="#f57d36" boxSize={4} />
                  <Text fontSize="md" fontWeight="semibold">Business Profile</Text>
                </HStack>
                <Grid templateColumns={{ base: "1fr", md: "repeat(2, 1fr)" }} gap={4}>
                  <InfoCard
                    icon={FaTags}
                    label="Category"
                    value={accountData.profile.category?.toUpperCase()}
                  />
                  <InfoCard
                    icon={FaTags}
                    label="Subcategory"
                    value={accountData.profile.subcategory?.toUpperCase()}
                  />
                </Grid>
              </Box>
            )}

            {/* Address Information */}
            {address && (
              <>
                <Divider />
                <Box>
                  <HStack spacing={2} mb={4}>
                    <Icon as={FaMapMarkerAlt} color="#f57d36" boxSize={4} />
                    <Text fontSize="md" fontWeight="semibold">Registered Address</Text>
                  </HStack>
                  <InfoCard
                    icon={FaMapMarkerAlt}
                    label="Complete Address"
                    value={fullAddress}
                  />
                </Box>
              </>
            )}

            <Divider />

            {/* Account Metadata */}
            <Box>
              <HStack spacing={2} mb={4}>
                <Icon as={FaCalendarAlt} color="#f57d36" boxSize={4} />
                <Text fontSize="md" fontWeight="semibold">Account Details</Text>
              </HStack>
              <Grid templateColumns={{ base: "1fr", md: "repeat(2, 1fr)" }} gap={4}>
                <InfoCard
                  icon={FaIdCard}
                  label="Account ID"
                  value={accountData.id}
                />
                <InfoCard
                  icon={FaCalendarAlt}
                  label="Created At"
                  value={formatDate(accountData.created_at)}
                />
              </Grid>
            </Box>
          </VStack>
        </ModalBody>

        <ModalFooter pt={4} borderTop="1px solid" borderColor={borderColor}>
          <Button
            variant="outline"
            mr={3}
            onClick={onClose}
            borderRadius="xl"
            px={6}
          >
            Close
          </Button>
          {/* <Button
            bg="#f57d36"
            color="white"
            _hover={{ bg: "#e56a2f" }}
            borderRadius="xl"
            px={6}
          >
            Edit Account
          </Button> */}
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};

export default LinkedAccountDetailsModal;
